import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

interface DropdownItem {
  path: string;
  label: string;
  description?: string;
}

interface NavDropdownProps {
  label: string;
  items: DropdownItem[];
  isActive?: boolean;
}

export function NavDropdown({ label, items, isActive = false }: NavDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { theme } = useTheme();
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<number>();

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const isItemActive = (path: string) => location.pathname === path;
  const isAnyItemActive = items.some(item => isItemActive(item.path));

  return (
    <div
      ref={dropdownRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        className={`flex items-center space-x-1 text-sm font-medium px-4 py-2 rounded-full transition-all duration-300 ${
          isActive || isAnyItemActive
            ? theme === 'dark'
              ? 'text-white bg-dark-300'
              : 'text-primary-600 bg-primary-50'
            : theme === 'dark'
              ? 'text-gray-300 hover:text-white hover:bg-dark-300'
              : 'text-gray-600 hover:text-primary-600 hover:bg-primary-50'
        }`}
      >
        <span>{label}</span>
        <ChevronDown 
          className={`w-4 h-4 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {/* Dropdown Menu */}
      <div
        className={`absolute top-full left-0 mt-2 w-64 transition-all duration-200 ${
          isOpen 
            ? 'opacity-100 translate-y-0 pointer-events-auto' 
            : 'opacity-0 translate-y-2 pointer-events-none'
        }`}
      >
        <div className={`rounded-xl border shadow-lg overflow-hidden ${
          theme === 'dark'
            ? 'bg-dark-200 border-dark-300'
            : 'bg-white border-gray-200'
        }`}>
          {items.map((item, index) => (
            <Link
              key={item.path}
              to={item.path}
              className={`block px-4 py-3 transition-colors duration-200 ${
                isItemActive(item.path)
                  ? theme === 'dark'
                    ? 'bg-primary-600 text-white'
                    : 'bg-primary-50 text-primary-600'
                  : theme === 'dark'
                    ? 'text-gray-300 hover:bg-dark-300 hover:text-white'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'
              } ${index !== items.length - 1 ? 'border-b' : ''} ${
                theme === 'dark' ? 'border-dark-300' : 'border-gray-100'
              }`}
              onClick={() => setIsOpen(false)}
            >
              <div className="font-medium">{item.label}</div>
              {item.description && (
                <div className={`text-xs mt-1 ${
                  isItemActive(item.path)
                    ? theme === 'dark'
                      ? 'text-primary-200'
                      : 'text-primary-500'
                    : theme === 'dark'
                      ? 'text-gray-400'
                      : 'text-gray-500'
                }`}>
                  {item.description}
                </div>
              )}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
